import type { ValueOf } from "@endgame/utils/types";
import type { ZeytalCLIPureConfig } from "@zeytal/kit";
import type { ZeytalTokens } from "@zeytal/types";

import { checkStringRegExpMatch } from "../../utils/regexp.ts";
import {
  styleNameStartKeyword,
  variableAliasKeyword,
  varNameStartKeyword,
} from "../../utils/tokens.ts";

type ZeytalConfigTwNamespaceKey =
  keyof ZeytalCLIPureConfig["tailwind"]["namespaces"];

type ZeytalConfigTwNamespaceConfig =
  ZeytalCLIPureConfig["tailwind"]["namespaces"][ZeytalConfigTwNamespaceKey];

type FilteredTokensSpaces = {
  zeytalTwRoot: ZeytalTokens.TransformedTokensArray; // Specific prop naming to avoid clashing w/ users modes
  zeytalTwTheme: ZeytalTokens.TransformedTokensArray; // Specific prop naming to avoid clashing w/ users modes
  [x: string]: ZeytalTokens.TransformedTokensArray;
};

const configToTwNamespacePrefixes: Record<ZeytalConfigTwNamespaceKey, string> =
  {
    fontFamily: "font-",
    fontSize: "text-",
    fontWeight: "font-weight-",
    letterSpacing: "tracking-",
    lineHeight: "leading-",
    color: "color-",
    spacing: "spacing-",
    breakpoint: "breakpoint-",
    container: "container-",
    radius: "radius-",
    shadow: "shadow-",
    insetShadow: "inset-shadow-",
    dropShadow: "drop-shadow-",
    blur: "blur-",
    perspective: "perspective-",
    aspect: "aspect-",
    ease: "ease-",
    animate: "animate-",
  };

let twNamespacesConfig: [string, ZeytalConfigTwNamespaceConfig][];

let twGlobalRewriteTokenNames: [
  string,
  ZeytalCLIPureConfig["tailwind"]["globalRewriteTokenNames"][string],
][];

const formatName = (
  userConfig: ZeytalCLIPureConfig,
  name: ZeytalTokens.TransformedToken["name"],
  namespaceMapName: boolean,
) => {
  const tokensPrefix =
    typeof userConfig.tailwind.tokensPrefix === "string"
      ? userConfig.tailwind.tokensPrefix
      : "";

  // Replacing the var/style prefix to avoid lengthy token names when using tailwind
  let formattedName = name
    .replace(varNameStartKeyword, "")
    .replace(styleNameStartKeyword, "");

  // Strip token name based on globalTokenNamesStrip strings
  formattedName = userConfig.tailwind.globalStripTokenNames.reduce(
    (acc, matcherStr) => acc.replace(new RegExp(matcherStr), ""),
    formattedName,
  );

  // Rewrite token name based on globalRewriteTokenNames strings
  formattedName = twGlobalRewriteTokenNames.reduce(
    (acc, [matcherStr, replacementStr]) =>
      acc.replace(new RegExp(matcherStr), replacementStr),
    formattedName,
  );

  if (namespaceMapName) {
    twNamespacesConfig.forEach((namespace) => {
      const [namespaceKey, namespaceConfig] = namespace as [
        ZeytalConfigTwNamespaceKey,
        ZeytalConfigTwNamespaceConfig,
      ];

      const twNamespacePrefix = configToTwNamespacePrefixes[namespaceKey];

      namespaceConfig.tokensMapping.forEach((mappingMatcherStr) => {
        if (checkStringRegExpMatch(mappingMatcherStr, formattedName)) {
          // Strip token name based on namespaced stripTokenNames strings
          formattedName = namespaceConfig.stripTokenNames.reduce(
            (acc, matcherStr) => acc.replace(new RegExp(matcherStr), ""),
            formattedName,
          );

          // Rewrite token name based on namespaced rewriteTokenNames strings
          formattedName = Object.entries(
            namespaceConfig.rewriteTokenNames,
          ).reduce(
            (acc, [matcherStr, replacementStr]) =>
              acc.replace(new RegExp(matcherStr), replacementStr),
            formattedName,
          );

          formattedName = twNamespacePrefix + tokensPrefix + formattedName;
        }
      });
    });
  } else {
    formattedName = tokensPrefix + formattedName;
  }

  return `--${formattedName}`;
};

const booleanValue = (boolean: boolean) => `${boolean}`;

const numberValue = (number: number) => number.toString();

const stringValue = (
  userConfig: ZeytalCLIPureConfig,
  name: string,
  value: string,
  originType: ZeytalTokens.TransformedToken["originType"],
) => {
  let cleanString = value;

  if (value.startsWith(variableAliasKeyword)) {
    cleanString = `var(${formatName(userConfig, value.replace(variableAliasKeyword, ""), false)})`;
  } else if (
    originType === "STRING" ||
    (originType === "TEXT" && name.endsWith("font-family"))
  ) {
    cleanString = `"${value}"`;
  } else {
    cleanString = value === "" ? '""' : value;
  }

  return cleanString;
};

const formatValue = (
  userConfig: ZeytalCLIPureConfig,
  token: ZeytalTokens.TransformedToken,
): string => {
  switch (typeof token.value) {
    case "boolean":
      return booleanValue(token.value);
    case "number":
      return numberValue(token.value);
    case "string":
      return stringValue(userConfig, token.name, token.value, token.originType);
    default:
      return "null";
  }
};

const filterTokensBySpaces = (
  userConfig: ZeytalCLIPureConfig,
  tokens: ZeytalTokens.TransformedTokensArray,
) => {
  const tokensMapping = twNamespacesConfig.flatMap(([, v]) => v.tokensMapping);

  const filteredTokens = tokens.reduce(
    (acc, token) => {
      const tokenCopy = JSON.parse(JSON.stringify(token));

      // Ignore tokens globally based on globalIgnoreTokenNames strings
      const shouldIgnore =
        userConfig.tailwind.globalIgnoreTokenNames.filter((matcherStr) =>
          checkStringRegExpMatch(matcherStr, tokenCopy.name),
        ).length > 0;

      if (shouldIgnore) {
        return acc;
      }

      const globalModeName = userConfig.tailwind.globalModes.reduce(
        (acc1, modeKeywords) => {
          const keywordMatch = modeKeywords.reduce(
            (acc2, keyword) =>
              tokenCopy.name.endsWith(keyword) ? keyword : acc2,
            "",
          );

          return keywordMatch === "" ? acc1 : keywordMatch;
        },
        "",
      );

      // Place tokens in the right tailwind properties category: :root or @theme
      const isThemeToken =
        tokensMapping.filter((matcherStr) =>
          checkStringRegExpMatch(matcherStr, tokenCopy.name),
        ).length > 0;

      const fileTokenDefSpace =
        globalModeName !== ""
          ? globalModeName
          : isThemeToken
            ? "zeytalTwTheme"
            : "zeytalTwRoot";

      // Remove the mode from the token name if one is detected
      if (globalModeName !== "") {
        tokenCopy.name = tokenCopy.name.replace(globalModeName, "");
      }

      // Push the token def to the right space
      if (!acc[fileTokenDefSpace]) {
        acc[fileTokenDefSpace] = [];
      }
      acc[fileTokenDefSpace].push(tokenCopy);

      // If the token belongs to a specific mode and to tailwind theme namespaces,
      // add it to both so @theme references the css var defined in its mode
      if (globalModeName !== "" && isThemeToken) {
        let tokenValue = tokenCopy.value;
        if (isThemeToken) {
          // Add the theme token with a ref its mode css var
          tokenValue = `${variableAliasKeyword}${tokenCopy.name}`;
        }

        acc.zeytalTwTheme.push({ ...tokenCopy, value: tokenValue });
      }

      return acc;
    },
    {
      zeytalTwRoot: [],
      zeytalTwTheme: [],
    } as FilteredTokensSpaces,
  );

  // Removing duplicates from the theme tokens array
  filteredTokens.zeytalTwTheme = Array.from(
    new Map(
      filteredTokens.zeytalTwTheme.map((item) => [item.name, item]),
    ).values(),
  );

  return filteredTokens;
};

const makeTokenSpaces = (
  userConfig: ZeytalCLIPureConfig,
  tokensModeKey: string,
  tokensModeSpace: ValueOf<FilteredTokensSpaces>,
) => {
  const cleanedUpModeKey = tokensModeKey.replace(/^-+|-+$/g, ""); // Remove leading and trailing hyphens
  const shouldNamespaceMapName = tokensModeKey === "zeytalTwTheme";
  const doubleSpaceChars = "  ";
  let spaceDefStart = "";
  let spaceDefEnd = "";
  let defSpace = "";

  if (tokensModeKey === "zeytalTwRoot") {
    spaceDefStart = ":root {\n";
    spaceDefEnd = "\n}";
  } else if (tokensModeKey === "zeytalTwTheme") {
    spaceDefStart = "@theme {\n";
    spaceDefEnd = "\n}";
  } else {
    spaceDefStart = `.${cleanedUpModeKey} {\n`;
    spaceDefEnd = "\n}";
  }

  const spaceLines = tokensModeSpace
    .map((token) => {
      return `${doubleSpaceChars}${formatName(userConfig, token.name, shouldNamespaceMapName)}: ${formatValue(userConfig, token)};`;
    })
    .join("\n");

  defSpace = `${spaceDefStart}${spaceLines}${spaceDefEnd}`;

  return defSpace;
};

export const translateTwV4 = (
  tokens: ZeytalTokens.TransformedTokensArray,
  userConfig: ZeytalCLIPureConfig,
) => {
  twNamespacesConfig = Object.entries(userConfig.tailwind.namespaces);
  twGlobalRewriteTokenNames = Object.entries(
    userConfig.tailwind.globalRewriteTokenNames,
  );

  const tokensSpaces = filterTokensBySpaces(userConfig, tokens);

  const tokensSpacesEntries = Object.entries(tokensSpaces).sort(
    ([tokensModeKeyA], [tokensModeKeyB]) => {
      // Put zeytalTwRoot first
      if (tokensModeKeyA === "zeytalTwRoot") return -1;
      if (tokensModeKeyB === "zeytalTwRoot") return 1;

      // Put zeytalTwTheme last
      if (tokensModeKeyA === "zeytalTwTheme") return 1;
      if (tokensModeKeyB === "zeytalTwTheme") return -1;

      // Preserve original order for non-special keys
      return 0;
    },
  );

  return tokensSpacesEntries
    .map(([tokensModeKey, tokensModeSpaces]) =>
      makeTokenSpaces(userConfig, tokensModeKey, tokensModeSpaces),
    )
    .join("\n\n");
};
